/**
 * Duplicate-account warning splash (AUTH-154).
 *
 * Shown as a blocking modal on the auth gate screens (/2fa, /step-up,
 * /enroll-mfa) so a duplicate-flagged account is warned BEFORE grinding
 * through the MFA step, not after. Mirrors the /duplicate-account
 * interstitial copy; that route remains the post-auth safety net for flows
 * with no gate screen (e.g. one-step passkey login).
 *
 * Only dismissable via the two actions — `open` is controlled and no
 * onOpenChange is passed, so overlay clicks and Escape do nothing.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@better-bookkeeping/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@better-bookkeeping/ui/alert";
import { AlertTriangle } from "lucide-react";

export function DuplicateAccountWarningModal({
  open,
  canonicalEmail,
  currentEmail,
  onProceed,
  onUseOtherAccount,
  "data-testid": testId,
}: {
  open: boolean;
  /** Canonical account email — masked when shown pre-2FA (no session yet). */
  canonicalEmail: string | null;
  /** The flagged account's own email, when the screen knows it. */
  currentEmail?: string;
  onProceed: () => void;
  onUseOtherAccount: () => void;
  /**
   * Caller-supplied and scoped to the host screen (/2fa, /step-up,
   * /enroll-mfa). Goes on AlertDialogContent — AlertDialog itself is a Radix
   * root and renders no element, so an id there is silently dropped. The two
   * actions derive from it, and they are the only exits: `open` is controlled
   * with no onOpenChange, so Escape and overlay clicks do nothing.
   */
  "data-testid"?: string;
}) {
  const canonical = canonicalEmail ?? "your main account";

  return (
    <AlertDialog open={open}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid={testId ? `${testId}-title` : undefined}>
            This account is a duplicate
          </AlertDialogTitle>
        </AlertDialogHeader>
        {/* Same warning treatment as the /duplicate-account interstitial. */}
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Use your main account</AlertTitle>
          <AlertDescription>
            The login {currentEmail ?? "you used"} has been flagged as a duplicate of {canonical}.
            To see the most comprehensive and up-to-date data, please log in with {canonical}.
          </AlertDescription>
        </Alert>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={onProceed}
            data-testid={testId ? `${testId}-proceed-button` : undefined}
          >
            Proceed anyway
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onUseOtherAccount}
            data-testid={testId ? `${testId}-use-main-account-button` : undefined}
          >
            Use my main account
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
